/**
 * React hook that subscribes to Firebase Auth state changes and exposes a
 * package-owned `CurrentUser` shape. Callers never see Firebase's `User` type.
 */

import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { getAuthInstance } from './client';
import { type CurrentUser, UserAccessError } from './types';

export interface UseAuthUserResult {
  user: CurrentUser | null;
  ready: boolean;
}

export function useAuthUser(): UseAuthUserResult {
  const [state, setState] = useState<UseAuthUserResult>({ user: null, ready: false });

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    (async () => {
      let auth;
      try {
        auth = getAuthInstance();
      } catch (err) {
        // Not initialized (or misconfigured) — let the consumer decide what to
        // render; we surface `ready: true` with `user: null` rather than
        // spinning forever. Log once so the error isn't silent during dev.
        if (err instanceof UserAccessError && err.code === 'not-initialized') {
          // eslint-disable-next-line no-console
          console.error('[user-access] useAuthUser called before initUserAccess(firebaseConfig).');
        } else {
          // eslint-disable-next-line no-console
          console.error(
            '[user-access] useAuthUser init error:',
            err instanceof Error ? err.message : String(err)
          );
        }
        if (!cancelled) {
          setState({ user: null, ready: true });
        }
        return;
      }

      try {
        await auth.authStateReady();
      } catch {
        if (!cancelled) {
          setState({ user: null, ready: true });
        }
        return;
      }

      if (cancelled) {
        return;
      }

      unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        if (!firebaseUser) {
          setState({ user: null, ready: true });
          return;
        }
        setState({
          user: {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            emailVerified: firebaseUser.emailVerified,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
          },
          ready: true,
        });
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  return state;
}
